import * as React from "react";

/**
 * ThemeProvider
 *
 * Canonical reference: ianfiles/ui-design-system.md §5
 *
 * Stack-specific watch-out #6.4: the legacy app defaults to `dark`. We default
 * to `system` (`prefers-color-scheme`) on first visit and only commit a
 * stored preference once the user toggles. This is less surprising and
 * costs one `matchMedia` listener.
 *
 * Persistence:
 *   - `localStorage["theme"]` holds `"light" | "dark" | "system"`.
 *   - The chosen-theme class is added to `<html>` (`document.documentElement`)
 *     because Tailwind's `darkMode: ["class"]` config inspects the root html
 *     element.
 *
 * We deliberately do NOT add a `data-theme` attribute (only the class) so we
 * stay aligned with shadcn defaults and avoid double-sourcing the theme state.
 */
export type Theme = "light" | "dark" | "system";

interface ThemeProviderState {
  theme: Theme;
  /** The resolved theme actually rendered — never `system`. */
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const STORAGE_KEY = "theme";

const ThemeContext = React.createContext<ThemeProviderState | undefined>(
  undefined
);

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>(() => readStoredTheme());
  const [systemTheme, setSystemTheme] = React.useState<"light" | "dark">(
    () => getSystemTheme()
  );

  // Subscribe to OS-level changes so a user flipping their system theme while
  // we're set to `system` updates the document class without a reload.
  React.useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) =>
      setSystemTheme(e.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const resolvedTheme: "light" | "dark" =
    theme === "system" ? systemTheme : theme;

  // Apply the resolved class to <html>
  React.useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = React.useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be unavailable (Safari private mode) — silently
      // continue, the in-memory state still drives the render.
    }
  }, []);

  const toggle = React.useCallback(() => {
    // Toggle ignores `system` — flipping moves the user to an explicit choice
    // based on the currently resolved theme.
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  }, [resolvedTheme, setTheme]);

  const value: ThemeProviderState = {
    theme,
    resolvedTheme,
    setTheme,
    toggle,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeProviderState {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}
